import React, { useEffect, useState } from "react"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { CheckIcon } from "@heroicons/react/20/solid"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { TAbrandCategories } from "../../../../services/userAPI"

interface SubCategory {
  name: string
  count: number
}

interface Category {
  main_category: string
  sub_categories: SubCategory[]
}

export interface ClassificationItem {
  main_category: {
    category: string
    confidence: number
  }
  sub_categories: {
    category: string
    confidence: number
  }[]
}

export type Classification = ClassificationItem[]

interface ClassificationModalProps {
  title: string
  isOpen: boolean
  onClose: () => void
  onSelect: () => void
  selectedClassification?: Classification | null
  setSelectedClassification: (classification: Classification) => void
}

const ClassificationModal: React.FC<ClassificationModalProps> = ({
  title,
  isOpen,
  onClose,
  onSelect,
  selectedClassification,
  setSelectedClassification,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [categories, setCategories] = useState<Category[]>([])

  const classificationArray = selectedClassification ?? []

  const currentMainCategory =
    classificationArray.length > 0 ? classificationArray[0]?.main_category?.category : null
  const currentSubCategories =
    classificationArray.length > 0
      ? classificationArray.flatMap((item) => item.sub_categories || [])
      : []

  const uniqueSubCategories = Array.from(
    new Map(currentSubCategories.map((sub) => [sub.category, sub])).values(),
  )

  useEffect(() => {
    if (isOpen) {
      fetchUserCategories()
    }
  }, [isOpen])

  const fetchUserCategories = async () => {
    const response = await TAbrandCategories()
    if (response && response.data) {
      setCategories(response.data)
    }
  }

  const filteredCategories = categories
    .map((category) => {
      const filteredSubs = category.sub_categories.filter((sub) =>
        sub.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      return { ...category, sub_categories: filteredSubs }
    })
    .filter((category) => category.sub_categories.length > 0)

  const handleCategorySelect = (mainCategory: string, subCategory: string) => {
    const isAlreadySelected = currentSubCategories.some((sub) => sub.category === subCategory)

    if (currentMainCategory && currentMainCategory !== mainCategory) {
      const newClassification: ClassificationItem = {
        main_category: {
          category: mainCategory,
          confidence: 1.0,
        },
        sub_categories: [
          {
            category: subCategory,
            confidence: 1.0,
          },
        ],
      }
      setSelectedClassification([newClassification])
      return
    }

    if (isAlreadySelected) {
      const updatedSubCategories = currentSubCategories.filter(
        (sub) => sub.category !== subCategory,
      )

      if (updatedSubCategories.length === 0) {
        setSelectedClassification([])
      } else {
        const updatedClassification: ClassificationItem = {
          main_category: {
            category: mainCategory,
            confidence: 1.0,
          },
          sub_categories: updatedSubCategories,
        }
        setSelectedClassification([updatedClassification])
      }
    } else {
      const newSubCategories = [
        ...currentSubCategories,
        {
          category: subCategory,
          confidence: 1.0,
        },
      ]

      const updatedClassification: ClassificationItem = {
        main_category: {
          category: mainCategory,
          confidence: 1.0,
        },
        sub_categories: newSubCategories,
      }
      setSelectedClassification([updatedClassification])
    }
  }

  const isSelected = (mainCategory: string, subCategory: string) => {
    return (
      currentMainCategory === mainCategory &&
      currentSubCategories.some((sub) => sub.category === subCategory)
    )
  }

  const isMainCategoryFullySelected = (mainCategory: string) => {
    const category = categories.find((cat) => cat.main_category === mainCategory)
    if (!category || category.sub_categories.length === 0) {
      return false
    }

    const allSubCategoryNames = category.sub_categories.map((sub) => sub.name)
    const selectedSubCategoryNames = currentSubCategories.map((sub) => sub.category)
    return (
      currentMainCategory === mainCategory &&
      allSubCategoryNames.every((sub) => selectedSubCategoryNames.includes(sub))
    )
  }

  const handleMainCategorySelect = (mainCategory: string) => {
    const category = categories.find((cat) => cat.main_category === mainCategory)
    if (!category) {
      return
    }

    const allSubCategoryNames = category.sub_categories.map((sub) => sub.name)
    const selectedSubCategoryNames = currentSubCategories.map((sub) => sub.category)
    const allSelected = allSubCategoryNames.every((sub) => selectedSubCategoryNames.includes(sub))

    if (allSelected && currentMainCategory === mainCategory) {
      setSelectedClassification([])
    } else {
      const allSubCategories = category.sub_categories.map((sub) => ({
        category: sub.name,
        confidence: 1.0,
      }))

      const newClassification: ClassificationItem = {
        main_category: {
          category: mainCategory,
          confidence: 1.0,
        },
        sub_categories: allSubCategories,
      }
      setSelectedClassification([newClassification])
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed right-0 top-0 h-full z-50">
      <div className="bg-white h-full w-96 shadow-lg flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold">{title}</h2>
              <p className="text-sm opacity-90 mt-1">Sınıflandırma Seçimi</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-y-auto">
          {/* Search Input */}
          <div className="relative w-full mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Kategori Ara"
              className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-2xl border-none focus:ring-0 focus:outline-none"
            />
          </div>

          {/* Categories List */}
          <div className="space-y-2">
            {filteredCategories.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">Kategori Bulunamadı</div>
            ) : (
              filteredCategories.map((category) => (
                <div key={category.main_category} className="space-y-2">
                  <h3
                    className="font-semibold text-gray-800 text-sm cursor-pointer hover:text-blue-600 flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={() => handleMainCategorySelect(category.main_category)}
                  >
                    {category.main_category}
                    {isMainCategoryFullySelected(category.main_category) && (
                      <CheckIcon className="w-4 h-4 text-blue-600" />
                    )}
                  </h3>
                  <div className="space-y-1 ml-4">
                    {category.sub_categories.map((subCategory) => (
                      <div
                        key={subCategory.name}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                        onClick={() =>
                          handleCategorySelect(category.main_category, subCategory.name)
                        }
                      >
                        <span className="text-sm text-gray-700">{subCategory.name}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">({subCategory.count})</span>
                          {isSelected(category.main_category, subCategory.name) && (
                            <CheckIcon className="w-4 h-4 text-blue-600" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {currentMainCategory
                ? `${currentMainCategory} - ${uniqueSubCategories.length} alt kategori seçili`
                : "Sınıflandırma seçilmedi"}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={() => {
                  onSelect()
                  onClose()
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClassificationModal
